import React, { useEffect, useState } from "react";
import { FaTimes } from "react-icons/fa";
import BookedRoom from "../../models/BookedRoom";
import Room from "../../models/Room";
import { attachCurrentTime, dateDuration, formatDateForInput, getBookingBusinessDate } from "../../utils/date";
import { initAllStores } from "../../utils/cache";
import { useModal } from "../../context/ConfirmModalContext";
import { checkRoomAvailability } from "../../utils/booking-helper";
import { useToast } from "../../context/ToastContext";
import { v4 as uuidv4 } from "uuid";
import { getUserHotel } from "../../utils/user-helper";
import { terminal } from "../../utils/user-helper";
import FullPageSpinner from "../../components/FullPageSpinner";

interface AddEditBookingModalProps {
  show: boolean;
  isEdit: boolean;
  booking: BookedRoom & { bookingNo: string, bookingId?: string };
  onClose: () => void;
  onSave: (
    updatedBooking: BookedRoom,
    room: {
      new: Partial<Room>,
      old: Partial<Room>,
    }) => void;
}

interface ValidationResult {
  valid: boolean;
  message?: string;
}
const today = new Date().toISOString().split("T")[0];

const AddEditBookingModal = ({ show, isEdit, booking, onClose, onSave }: AddEditBookingModalProps) => {
  const oldRoomId = booking.roomId;
  const { openModal, closeModal } = useModal();
  const [guestName, setGuestName] = useState(booking.guestName);

    const [terminalId] = useState(() => terminal());
  // const { showToast } = useToast();

    const [loading, setLoading] = useState(false);

  const [checkInDate, setCheckInDate] = useState(
    booking?.checkInDate ? formatDateForInput(booking.checkInDate) : ""
  );
  const [checkOutDate, setCheckOutDate] = useState(
    booking?.checkOutDate ? formatDateForInput(booking.checkOutDate) : ""
  );

  const [paymentMethod, setPaymentMethod] = useState(booking.paymentMethod ?? "cash");
  const [paymentStatus, setPaymentStatus] = useState("paid");
  const [bookingType, setBookingType] = useState(
    booking.bookingType
      ? booking.bookingType.charAt(0).toLowerCase() + booking.bookingType.slice(1)
      : "fullTime"
  );
  const [guestNumber, setGuestNumber] = useState(booking.guestNumber ?? "");
  const [roomId, setRoomId] = useState({ new: booking?.roomId, old: oldRoomId });
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const [duration, setDuration] = useState(booking?.duration ?? 0);
  const [discount, setDiscount] = useState(booking?.discount ?? 0);
  const [totalAmount, setTotalAmount] = useState(booking?.amount);
  const [error, setError] = useState<string | null>(null);

  // ---------------- Load All Rooms ----------------
  useEffect(() => {
    const loadAllRooms = async () => {
      const cache = await initAllStores();
      const rooms: Room[] | null = await cache.getAllBy("rooms", { terminal: terminalId }, "number");
      if (rooms) {
        const mappedRooms = rooms.map(room => ({
          id: room.id,
          name: room.name,
          number: room.number,
          cashPrice: room.cashPrice,
          isOccupied: room.isOccupied,
        }));
        setAllRooms(mappedRooms as Room[]);
      }
    };
    loadAllRooms();
  }, []);

  useEffect(() => {
    filterRooms();
  }, [bookingType])

  async function validateRoomSelection(
    allRooms: Room[],
    roomId: string,
    checkInDate: string,
    checkOutDate: string,

  ): Promise<ValidationResult> {
    if (!roomId) return { valid: false, message: "No room selected." };

    const cache = await initAllStores();
    const room = allRooms.find((r) => r.id === roomId);

    if (!room) {
      openModal(
        "Room Not Found ❌",
        "Selected room could not be found.",
        () => { }, false, "Close"
      );

      return { valid: false };
    }

    const bookedRooms: BookedRoom[] = await cache.getAllBy("bookedRooms", {
      isUse: true,
      roomId,
    });

    const conflictMsg = await checkRoomAvailability(
      bookedRooms, roomId, checkInDate, checkOutDate, booking.id
    );

    if (conflictMsg) {
      openModal("Room Unavailable ⚠️",
        conflictMsg, () => { }, false, "Close"
      );

      return { valid: false };
    }

    return { valid: true };
  }

  // ---------------- Auto Recalculate Total ----------------
  useEffect(() => {
    const calculateTotal = async () => {
      try {
        const data = await getUserHotel();
        if (!data) return null;
        const { hotel } = data;

        const isShortTime = bookingType.toLocaleLowerCase() === "shorttime";
        const isLateCheckout = bookingType.toLocaleLowerCase() === "latecheckout";

        if (checkInDate && checkOutDate && !isShortTime && roomId) {
          const duration = dateDuration(checkInDate, checkOutDate);
          setDuration(duration);

          const selectedRoom = allRooms.find(r => r.id === roomId.new);
          if (selectedRoom) {
            setTotalAmount(selectedRoom.cashPrice * (duration || 0));
          }
        }

        if (isShortTime || isLateCheckout) {
          setCheckInDate(today);
          setCheckOutDate(today);
          setDiscount(0);
          if (isShortTime) setTotalAmount(hotel.preference.shortTimeAmount);
          else  setTotalAmount(hotel.preference.lateCheckoutAmount);
        }
      } catch (err: any) {
        console.log(err);
      }
    };
    calculateTotal();
  }, [roomId, checkInDate, checkOutDate, allRooms, bookingType]);

  // ---------------- Validation ----------------
  const updateCheckInDate = (checkin: string) => {
    if (new Date(checkin) < new Date(today)) {
      // showToast("Check-in date cannot be in the past.", { type: "error" });
      setError("Check-in date cannot be in the past.");
      return;
    } if (new Date(checkin) > new Date(checkOutDate)) {
      // showToast("Check-in date cannot exceed checkout date.", { type: "error" });
      setError("Check-in date cannot exceed checkout date.");
      return;
    }
    setError(null);
    setCheckInDate(checkin);
  };

  const updateCheckOutDate = (checkout: string) => {
    if (!checkInDate) {
      // showToast("Please select a check-in date first.", { type: "error" });
      setError("Please select a check-in date first.");
      return;
    } else if (new Date(checkout) < new Date(checkInDate)) {
      // showToast("Check-out date must be after check-in date.", { type: "error" });
      setError("Check-out date must be after check-in date.");
      return;
    }
    setError(null);
    setCheckOutDate(checkout);
  };

  // ---------------- Save Handler ----------------
  const handleSave = async () => {
    if (!guestName.trim()) {
      // showToast("Guest name cannot be empty.", { type: "error" });
      setError("Guest name cannot be empty.");
      return;
    }
    if (new Date(checkOutDate) < new Date(checkInDate)) {
      setError("Check-out date must be after check-in date.");
      return;
    }

    if (!checkInDate) return setError("Please select a check-in date before proceeding.");
    else if (!checkOutDate) return setError("Please select a check-out date before proceeding.");
    else if (!roomId.new) return setError("Please select a room before proceeding.");
    else if (!paymentMethod) return setError("Please select a payment method before proceeding.");

    setError(null);

    const newRoom = allRooms.find((r: Room) => r.id === roomId.new);
    const oldRoom = allRooms.find((r: Room) => roomId.old && r.id === roomId.old);
    if ((!newRoom || !oldRoom) && isEdit) {
      return openModal(
        "Session Expired ⚠️",
        "We couldn’t retrieve the room details because your session data is out of sync. Please log in again so we can refresh your account information.",
        () => {
          closeModal();
          window.location.href = "/login";
        },
        false,
        "Log In Again"
      );
    }

    const result = await validateRoomSelection(
      allRooms,
      roomId.new,
      checkInDate,
      checkOutDate,
    );

    if (result.valid) {
      const updatedBooking = {
        ...booking,
        id: booking.id ?? uuidv4(),
        roomId: roomId.new,
        bookingId: booking.bookingId,
        roomNumber: newRoom?.number,
        amount: totalAmount,
        discount: !isNaN(discount) ? discount : 0,
        duration,
        guestName,
        ...isEdit ? {} : { isPaid: paymentStatus === "paid" },
        guestNumber,
        checkInDate: attachCurrentTime(checkInDate),
        checkOutDate,
        paymentMethod,
        isUse: true,
        bookingType: bookingType,
        // clearedById: paymentStatus === "paid" ? 
      };
      onSave(updatedBooking, { new: newRoom as Room, old: oldRoom as Room });
    }
  };

  if (!show) return null;

  const filterRooms = () => {
    const filteredRooms = (
      bookingType.toLocaleLowerCase() === "fulltime"
        || bookingType.toLocaleLowerCase() === "shorttime"
        ? allRooms.filter((r) => !r.isOccupied || r.id === booking.roomId)
        : allRooms
    );
    return filteredRooms;
  }

  const bookingNo = booking.bookingNo ? `#${booking.bookingNo}` : "";
  // ---------------- UI ----------------
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-2 sm:px-0">
               <FullPageSpinner show={loading} />
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-lg overflow-auto max-h-[90vh] animate-fadeIn">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 border-b">
          <h2 className="text-lg font-semibold">
            {isEdit ? "Edit Booking" : `Add Room to Booking ${bookingNo}`}
          </h2>

          <button onClick={onClose} className="text-gr ay-500 hover:text-gray-700">
            {FaTimes({ size: 18 })}
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-5">
          {error && <p className="text-red-600 text-sm">{error}</p>}

          {/* Guest Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Guest Name</label>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Guest Number (Optional)</label>
              <input
                type="text"
                placeholder="e.g., 1 male, 1 female etc."
                value={guestNumber}
                onChange={(e) => setGuestNumber(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Check-in</label>
              <input
                type="date"
                disabled={bookingType.toLocaleLowerCase() === "shorttime"}
                value={checkInDate}
                onChange={(e) => updateCheckInDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Check-out</label>
              <input
                type="date"
                disabled={bookingType.toLocaleLowerCase() === "shorttime"}
                value={checkOutDate}
                onChange={(e) => updateCheckOutDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Room, Payment, Booking Type & Discount */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Payment Status */}
            {!isEdit && <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Payment Status</label>
              <select
                value={paymentStatus}
                onChange={(e) => setPaymentStatus(e.target.value as PaymentStatus)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
              </select>
            </div>}

            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Payment Method</label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value as PaymentType)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="cash">Cash</option>
                <option value="pos">POS</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>

            {/* Booking Type */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Booking Type</label>
              <select
                value={bookingType}
                onChange={(e) => setBookingType(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="shortTime">ShortTime</option>
                <option value="fullTime">FullTime</option>
                <option value="reservation">Reservation</option>
                {isEdit && <option value="lateCheckOut">Late Checkout</option>}
              </select>
            </div>

            {/* Room */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Room</label>
              <select
                value={roomId.new}
                onChange={async (e) => {
                  const rmId = e.target.value;
                  setError(null);

                  if (!checkInDate) {
                    setError("Please select a check-in date first.");
                    e.target.value = "";
                    return;
                  }

                  if (!checkOutDate) {
                    setError("Please select a check-out date first.");
                    e.target.value = "";
                    return;
                  }

                  const result = await validateRoomSelection(allRooms, rmId, checkInDate, checkOutDate);

                  if (result.valid) {
                    setRoomId((prev) => ({ ...prev, new: rmId }));
                  } else {
                    e.target.value = "";
                  }
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              >
                <option value="">Select Room</option>
                {filterRooms().map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.number} - {room.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Discount */}
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">Discount (₦)</label>
              <input
                type="number"
                value={isNaN(discount) ? "" : discount}
                onChange={e => {
                  if (!isNaN(Number(e.target.value))) {
                    if (Number(e.target.value) > totalAmount) {
                      return openModal(
                        "Invalid Discount ⚠️",
                        "The discount cannot be greater than the total amount. Please enter a valid discount value.",
                        () => {
                          closeModal();
                        },
                        false,
                        "Close"
                      );
                    }
                    setDiscount(parseFloat(e.target.value));
                  }
                }}
                placeholder="Enter discount amount"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="px-4 py-3 border rounded-md">
            <h4 className="font-semibold text-gray-700 mb-2">Booking Summary</h4>
            <div className="flex justify-between text-sm">
              <span>Total Amount:</span>
              <span>₦{(totalAmount ?? 0).toLocaleString()}</span>
            </div>
            {(!isNaN(discount) && discount !== 0) && <div className="flex justify-between text-sm text-red-600">
              <span>Total Discount:</span>
              <span>-₦{(isNaN(discount ?? 0) ? 0 : discount ?? 0).toLocaleString()}</span>
            </div>}

            {(!isNaN(discount) && discount !== 0) && totalAmount > 0 && (<div className="flex justify-between text-sm text-green-700 font-semibold">
              <span>Net Payable:</span>
              <span>₦{(totalAmount - (isNaN(discount ?? 0) ? 0 : discount ?? 0)).toLocaleString()}</span>
            </div>)}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-3 bg-gray-50">
          <button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            {isEdit ? "Save Changes" : "Add Room to Booking"}
          </button>
          <button
            onClick={onClose}
            className="bg-gray-300 hover:bg-gray-400 text-black px-4 py-2 rounded-lg"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditBookingModal;
